import constants from '@/shared/constants/constants';
import {
  createAiGiProjectApi,
  downloadAiGiBlob,
  downloadAiGiReportsApi,
  downloadAiGiUtterancesApi,
  getAiGiProgressApi,
  getAiGiPastProjectsApi,
  getAiGiProjectApi,
  getAiGiResultApi,
  getAiGiUsageSummaryApi,
  resolveAiGiDownloadFilename,
  startAiGiResearchApi,
  suggestAiGiQuestionsApi,
  updateAiGiFormApi,
  validateAiGiFormApi,
  createResearchAvatarRealtimeCall,
  finalizeResearchAvatar,
  getResearchAvatarConfig,
} from '@/services/aiGroupInterview.service';
import { getSuggestProjectNameApi } from '@/services/project.service';
import {
  appendFormText,
  normalizeFormText,
} from '@/shared/helpers/aiGroupInterviewFormText';

const emptyForm = () => ({
  background: '',
  purpose: '',
  respondent_count: null,
  target_audience: '',
  desired_questions: '',
  supplement: '',
  consider_questions_for_persona: true,
});

const normalizeFormPayload = (form = {}) => {
  const count = form.respondent_count;
  return {
    ...form,
    respondent_count: (count === null || count === undefined || count === '')
      ? null
      : String(count),
  };
};

const state = {
  avatarConfig: null,
  usageSummary: null,
  pastProjects: [],
  projectId: null,
  projectName: '',
  form: emptyForm(),
  sourceProjectId: null,
  /** Menu choice at SelectType: 'new' | 'old' — not changed by old-project dropdown on NewProject. */
  registrationEntryType: null,
  draftProjectName: '',
  draftTargetCountry: null,
  loading: false,
  error: null,
  estimatedMinutes: null,
  progress: null,
  result: null,
};

const mutations = {
  SET_AVATAR_CONFIG(state, config) {
    state.avatarConfig = config;
  },
  SET_FORM_VALUES_FROM_AVATAR(state, formValues) {
    const form = { ...state.form };
    form.background = appendFormText(form.background, formValues.issue_or_background);
    form.purpose = appendFormText(form.purpose, formValues.usage_purpose);
    const count = normalizeFormText(formValues.number_of_interviewees);
    if (count) {
      form.respondent_count = count;
    }
    form.target_audience = appendFormText(form.target_audience, formValues.target_respondents);
    form.desired_questions = appendFormText(form.desired_questions, formValues.interview_questions);
    state.form = form;
  },
  AI_GI_SET_USAGE(s, payload) {
    s.usageSummary = payload;
  },
  AI_GI_SET_PAST(s, payload) {
    s.pastProjects = payload || [];
  },
  AI_GI_SET_PROJECT(s, payload) {
    if (payload.id !== undefined) s.projectId = payload.id;
    if (payload.name !== undefined) s.projectName = payload.name;
    if (payload.form !== undefined) s.form = { ...emptyForm(), ...payload.form };
  },
  AI_GI_SET_SOURCE(s, id) {
    s.sourceProjectId = id;
  },
  AI_GI_SET_REGISTRATION_ENTRY_TYPE(s, entryType) {
    s.registrationEntryType = entryType === 'new' || entryType === 'old' ? entryType : null;
  },
  AI_GI_SET_DRAFT(s, payload) {
    if (payload.projectName !== undefined) s.draftProjectName = payload.projectName;
    if (payload.targetCountry !== undefined) s.draftTargetCountry = payload.targetCountry;
    if (payload.sourceProjectId !== undefined) s.sourceProjectId = payload.sourceProjectId;
    if (payload.form !== undefined) s.form = { ...emptyForm(), ...payload.form };
  },
  AI_GI_SET_FORM(s, form) {
    s.form = { ...emptyForm(), ...form };
  },
  AI_GI_SET_LOADING(s, v) {
    s.loading = v;
  },
  AI_GI_SET_ERROR(s, v) {
    s.error = v;
  },
  AI_GI_SET_ESTIMATED_MINUTES(s, v) {
    s.estimatedMinutes = v;
  },
  AI_GI_SET_PROGRESS(s, payload) {
    s.progress = payload;
  },
  AI_GI_SET_RESULT(s, payload) {
    s.result = payload;
  },
  AI_GI_RESET(s) {
    s.avatarConfig = null;
    s.usageSummary = null;
    s.pastProjects = [];
    s.projectId = null;
    s.projectName = '';
    s.form = emptyForm();
    s.sourceProjectId = null;
    s.registrationEntryType = null;
    s.draftProjectName = '';
    s.draftTargetCountry = null;
    s.loading = false;
    s.error = null;
    s.estimatedMinutes = null;
    s.progress = null;
    s.result = null;
  },
};

const actions = {
  async fetchUsageSummary({ commit }) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    const { status, data } = await getAiGiUsageSummaryApi();
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_OK) {
      commit('AI_GI_SET_USAGE', data);
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return null;
  },
  async fetchPastProjects({ commit }) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    const { status, data } = await getAiGiPastProjectsApi();
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_OK) {
      commit('AI_GI_SET_PAST', data);
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return [];
  },
  setDraft({ commit, state: s }, {
    projectName,
    targetCountry,
    sourceProjectId,
  }) {
    const sourceId = sourceProjectId || null;
    const prevSourceId = s.sourceProjectId || null;
    const sourceChanged = Number(sourceId || 0) !== Number(prevSourceId || 0);
    const draftChanged = sourceChanged
      || (projectName || '') !== (s.draftProjectName || '')
      || Number(targetCountry) !== Number(s.draftTargetCountry);

    const isEmptyForm = (form) => {
      if (!form) return true;
      return !(
        (form.background || '').trim()
        || (form.purpose || '').trim()
        || (form.target_audience || '').trim()
        || (form.desired_questions || '').trim()
        || (form.supplement || '').trim()
        || form.respondent_count != null
      );
    };

    let form = { ...emptyForm(), ...(s.form || {}) };
    if (sourceId) {
      const past = (s.pastProjects || []).find(
        item => Number(item.id) === Number(sourceId),
      );
      if (sourceChanged || isEmptyForm(s.form)) {
        form = past && past.form
          ? { ...emptyForm(), ...past.form }
          : emptyForm();
      }
    } else if (sourceChanged) {
      form = emptyForm();
    }

    commit('AI_GI_SET_DRAFT', {
      projectName: projectName || '',
      targetCountry,
      sourceProjectId: sourceId,
      form,
    });
    commit('AI_GI_SET_PROJECT', {
      id: draftChanged ? null : s.projectId,
      name: projectName || '',
      form,
    });
  },
  async createProject({ commit, state: s }, {
    name,
    targetCountry = null,
    sourceProjectId = undefined,
  } = {}) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    const sourceId = sourceProjectId !== undefined ? sourceProjectId : s.sourceProjectId;
    if (sourceId !== undefined && sourceId !== s.sourceProjectId) {
      commit('AI_GI_SET_SOURCE', sourceId || null);
    }
    const payload = {
      name: name || s.draftProjectName,
      source_project_id: sourceId || null,
      target_country: targetCountry != null ? targetCountry : s.draftTargetCountry,
    };
    const { status, data } = await createAiGiProjectApi(payload);
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_CREATED || status === constants.HTTP_OK) {
      commit('AI_GI_SET_PROJECT', data);
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return null;
  },
  async createProjectWithForm({ dispatch, state: s }, form) {
    const created = await dispatch('createProject', {
      name: s.draftProjectName,
      targetCountry: s.draftTargetCountry,
      sourceProjectId: s.sourceProjectId,
    });
    if (!created || !created.id) {
      return null;
    }
    return dispatch('updateForm', {
      projectId: created.id,
      form: normalizeFormPayload(form),
      validateForConfirm: true,
    });
  },
  async validateForm({ commit }, { form, validateForConfirm = true } = {}) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    const payload = {
      ...normalizeFormPayload(form),
      validate_for_confirm: validateForConfirm,
    };
    const { status, data } = await validateAiGiFormApi(payload);
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_OK) {
      if (data && data.form) {
        commit('AI_GI_SET_FORM', data.form);
      }
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return null;
  },
  async fetchProject({ commit }, projectId) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    const { status, data } = await getAiGiProjectApi(projectId);
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_OK) {
      commit('AI_GI_SET_PROJECT', data);
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return null;
  },
  async updateForm({ commit }, { projectId, form, validateForConfirm = false }) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    const payload = {
      ...normalizeFormPayload(form),
      validate_for_confirm: validateForConfirm,
    };
    const { status, data } = await updateAiGiFormApi(projectId, payload);
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_OK) {
      commit('AI_GI_SET_PROJECT', data);
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return null;
  },
  async startResearch({ commit }, projectId) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    const { status, data } = await startAiGiResearchApi(projectId);
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_OK && data) {
      const minutes = Number(data.estimated_minutes);
      commit(
        'AI_GI_SET_ESTIMATED_MINUTES',
        Number.isFinite(minutes) && minutes > 0
          ? minutes
          : constants.AI_GI_ESTIMATED_MINUTES_PER_JOB,
      );
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return null;
  },
  async suggestQuestions({ commit }, payload) {
    commit('AI_GI_SET_ERROR', null);
    const { status, data } = await suggestAiGiQuestionsApi(payload);
    if (status === constants.HTTP_OK) {
      return data;
    }
    commit('AI_GI_SET_ERROR', data);
    return null;
  },
  async fetchProgress({ commit }, payload) {
    const projectId = payload && typeof payload === 'object'
      ? payload.projectId
      : payload;
    const silent = !!(payload && typeof payload === 'object' && payload.silent);
    commit('AI_GI_SET_ERROR', null);
    if (!silent) {
      commit('AI_GI_SET_PROGRESS', null);
      commit('AI_GI_SET_LOADING', true);
    }
    const { status, data } = await getAiGiProgressApi(projectId);
    if (!silent) {
      commit('AI_GI_SET_LOADING', false);
    }
    if (status === constants.HTTP_OK) {
      commit('AI_GI_SET_PROGRESS', data);
      return data;
    }
    commit('AI_GI_SET_ERROR', {
      ...(data && typeof data === 'object' && !Array.isArray(data) ? data : { message: data }),
      status,
    });
    return null;
  },
  async fetchResult({ commit }, projectId) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_RESULT', null);
    commit('AI_GI_SET_LOADING', true);
    const { status, data } = await getAiGiResultApi(projectId);
    commit('AI_GI_SET_LOADING', false);
    if (status === constants.HTTP_OK) {
      commit('AI_GI_SET_RESULT', data);
      if (data && data.name) {
        commit('AI_GI_SET_PROJECT', { name: data.name });
      }
      return data;
    }
    commit('AI_GI_SET_ERROR', {
      ...(data && typeof data === 'object' && !Array.isArray(data) ? data : { message: data }),
      status,
    });
    return null;
  },
  async downloadUtterances({ commit, state: s }, { projectId, format = 'txt', fallbackName } = {}) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    try {
      const { status, data, headers } = await downloadAiGiUtterancesApi(projectId, format);
      if (status === constants.HTTP_OK && data) {
        const projectName = (s.result && s.result.name) || s.projectName || 'project';
        const filename = resolveAiGiDownloadFilename(
          headers,
          fallbackName || `${projectName}_発言録.${format}`,
        );
        downloadAiGiBlob(data, filename);
        return true;
      }
      commit('AI_GI_SET_ERROR', {
        ...(data && typeof data === 'object' && !Array.isArray(data) ? data : { message: data }),
        status,
      });
      return false;
    } finally {
      commit('AI_GI_SET_LOADING', false);
    }
  },
  async downloadReport({ commit }, { projectId, format, fallbackName }) {
    commit('AI_GI_SET_ERROR', null);
    commit('AI_GI_SET_LOADING', true);
    try {
      const { status, data, headers } = await downloadAiGiReportsApi(projectId, format);
      if (status === constants.HTTP_OK && data) {
        const filename = resolveAiGiDownloadFilename(headers, fallbackName);
        downloadAiGiBlob(data, filename);
        return true;
      }
      commit('AI_GI_SET_ERROR', {
        ...(data && typeof data === 'object' && !Array.isArray(data) ? data : { message: data }),
        status,
      });
      return false;
    } finally {
      commit('AI_GI_SET_LOADING', false);
    }
  },
  setSourceProjectId({ commit }, id) {
    commit('AI_GI_SET_SOURCE', id);
  },
  setRegistrationEntryType({ commit }, entryType) {
    commit('AI_GI_SET_REGISTRATION_ENTRY_TYPE', entryType);
  },
  async fetchSuggestedProjectName({ commit }) {
    const { status, data } = await getSuggestProjectNameApi();
    if (status === constants.HTTP_OK && data && data.name) {
      commit('AI_GI_SET_DRAFT', { projectName: String(data.name).trim() });
      return String(data.name).trim();
    }
    return '';
  },
  setLocalForm({ commit }, form) {
    commit('AI_GI_SET_FORM', form);
  },
  /** Clears draft/form/source only; keeps registrationEntryType (menu New/Old highlight). */
  clearRegistrationDraft({ commit }) {
    commit('AI_GI_SET_DRAFT', {
      projectName: '',
      targetCountry: null,
      sourceProjectId: null,
      form: emptyForm(),
    });
    commit('AI_GI_SET_PROJECT', { id: null, name: '', form: emptyForm() });
  },
  /** New menu path: drop old-project copy source/form; keep draftProjectName (e.g. after suggest API). */
  clearOldProjectCopyForNewMenu({ commit }) {
    commit('AI_GI_SET_SOURCE', null);
    commit('AI_GI_SET_FORM', emptyForm());
  },
  reset({ commit }) {
    commit('AI_GI_RESET');
  },
  async fetchResearchAvatarConfig({ commit }) {
    const res = await getResearchAvatarConfig();
    if (res.ok) {
      commit('SET_AVATAR_CONFIG', res.data || {});
    }
    return res;
  },
  async finalizeResearchAvatar({ commit }, { messages }) {
    const res = await finalizeResearchAvatar(messages);
    if (res.ok && res.data && res.data.form_values) {
      commit('SET_FORM_VALUES_FROM_AVATAR', res.data.form_values);
    }
    return res;
  },
  async connectResearchAvatarRealtime(_ctx, { sdp, voice }) {
    return createResearchAvatarRealtimeCall(sdp, { voice });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
